import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowDown, Landmark, Scale, Printer, ChevronDown,
  Calculator, FileSearch, Vault,
} from "lucide-react";

const GOLD = "#eec643";
const VIOLET = "#7a5af8";

/* ================= house primitives (match the guide) ================= */
const Particles = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
    {Array.from({ length: 30 }).map((_, i) => (
      <span
        key={i}
        className="absolute rounded-full"
        style={{
          width: i % 3 === 0 ? 3 : 2,
          height: i % 3 === 0 ? 3 : 2,
          left: `${(i * 41) % 100}%`,
          top: `${(i * 59) % 100}%`,
          background: i % 4 === 0 ? VIOLET : GOLD,
          opacity: 0.2 + (i % 5) * 0.1,
          animation: `ivd-float ${8 + (i % 6)}s ease-in-out ${i * 0.4}s infinite`,
        }}
      />
    ))}
    <style>{`@keyframes ivd-float { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-40px) } }`}</style>
  </div>
);

const Kinetic = ({ words, className = "", delay = 0 }) => (
  <span className={className}>
    {words.split(" ").map((w, i) => (
      <motion.span
        key={i}
        className="inline-block mr-[0.28em]"
        initial={{ opacity: 0, y: 24, rotateX: -70 }}
        whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ delay: delay + i * 0.07, duration: 0.5, ease: [0.2, 1.4, 0.4, 1] }}
      >
        {w}
      </motion.span>
    ))}
  </span>
);

const BookHead = ({ kicker, title, sub, testid }) => (
  <div data-testid={testid} className="relative max-w-5xl mx-auto px-6 pt-24 pb-10">
    <span className="font-mono text-xs text-amber-500/80 border border-amber-500/30 rounded-full px-3 py-1">{kicker}</span>
    <h2 className="text-3xl sm:text-5xl font-bold text-stone-100 tracking-tight mt-6 mb-4" style={{ fontFamily: "Georgia, serif" }}>
      <Kinetic words={title} />
    </h2>
    <p className="text-stone-400 max-w-2xl leading-relaxed">{sub}</p>
  </div>
);

const Cite = ({ children }) => (
  <span className="font-mono text-[10px] text-amber-400/90 border border-amber-500/25 rounded px-2 py-0.5 mr-2 mb-1 inline-block">{children}</span>
);

/* ================= BOOK ONE data — every hop sourced ================= */
const HOPS = [
  {
    id: "obligor", icon: Scale, label: "THE OBLIGOR", sub: "where the dollar starts",
    body: "Federal law requires immediate income withholding in IV-D cases — the obligation leaves the paycheck before it reaches the worker. Nevada executes through NRS chapter 31A enforcement.",
    cites: ["42 U.S.C. § 666(a)(1)", "NRS ch. 31A"],
  },
  {
    id: "sdu", icon: Landmark, label: "STATE DISBURSEMENT UNIT", sub: "the single funnel",
    body: "Every IV-D collection in the state must pass through one centralized State Disbursement Unit. One ledger, one intake point — federally required, federally audited.",
    cites: ["42 U.S.C. § 654b"],
  },
  {
    id: "distribution", icon: ArrowDown, label: "DISTRIBUTION", sub: "who the collection pays",
    body: "Collected support is distributed by federal rule: current support to the family; in assistance cases, portions may be retained by the state as cost recovery. The split is statute, not discretion.",
    cites: ["42 U.S.C. § 657"],
  },
  {
    id: "match", icon: Calculator, label: "THE 66% MATCH", sub: "every admin dollar draws 66¢",
    body: "For every legitimate administrative dollar the state spends running child-support enforcement, the federal treasury pays 66 cents — in effect every fiscal year since 1990. The state carries 34.",
    cites: ["42 U.S.C. § 655(a)"],
  },
  {
    id: "incentive", icon: Scale, label: "THE INCENTIVE POOL", sub: "paid on performance",
    body: "On top of the match, states earn from a federal incentive pool scored on five measures: paternity establishment, order establishment, current collections (weighted 100%), arrears collections and cost-effectiveness (capped at 75%). Scores must survive federal audit, and payments must be reinvested in the program.",
    cites: ["42 U.S.C. § 658a", "45 C.F.R. pt. 305"],
  },
  {
    id: "registry", icon: Vault, label: "THE COURT REGISTRY", sub: "funds at rest can be invested",
    body: "Money that rests in a clerk's hands — disputed, undeliverable, or deposited amounts — does not necessarily sit idle. Comparative statute: a clerk of superior court may invest registry funds, and single accounts over $10,000 expected to rest more than six months must be invested within 60 days, in U.S. obligations, state obligations, or insured deposits. The State Auditor inspects for compliance; violation is a Class 1 misdemeanor. What the statute does not state on its face is where the yield posts — which is why the registry belongs in discovery.",
    cites: ["N.C.G.S. § 7A-112 (comparative)"],
  },
];

/* ================= Nevada formula ================= */
const RATES = { 1: [0.16, 0.08, 0.04], 2: [0.22, 0.11, 0.06], 3: [0.26, 0.13, 0.06], 4: [0.28, 0.14, 0.07] };
const BRACKET_SIZES = [6000, 4000, Infinity];
const BRACKET_LABELS = ["first $6,000", "$6,000 – $10,000", "over $10,000"];

function ratesFor(n) {
  if (n <= 4) return RATES[n];
  const r = [...RATES[4]];
  for (let i = 5; i <= n; i++) { r[0] += 0.02; r[1] += 0.01; r[2] += 0.005; }
  return r;
}

function obligationBreakdown(income, children) {
  const rates = ratesFor(children);
  let remaining = Math.max(0, income);
  return rates.map((rate, i) => {
    const inBracket = Math.min(remaining, BRACKET_SIZES[i]);
    remaining -= inBracket;
    return { label: BRACKET_LABELS[i], rate, base: inBracket, amount: inBracket * rate };
  });
}

const fmtUSD = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

const NevadaCalculator = () => {
  const [income, setIncome] = useState("");
  const [children, setChildren] = useState(1);
  const [shared, setShared] = useState(true);   // 50/50 joint custody is the goal state — on by default
  const [otherIncome, setOtherIncome] = useState("");
  const rows = useMemo(() => obligationBreakdown(parseFloat(income) || 0, children), [income, children]);
  const total = rows.reduce((s, r) => s + r.amount, 0);
  const otherTotal = useMemo(
    () => obligationBreakdown(parseFloat(otherIncome) || 0, children).reduce((s, r) => s + r.amount, 0),
    [otherIncome, children]);
  const offset = Math.abs(total - otherTotal);
  const payer = total >= otherTotal ? "the first income" : "the second income";
  return (
    <div data-testid="nv-calculator" className="rounded-2xl border border-amber-500/25 bg-stone-900/70 p-6 sm:p-8">
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.25em] text-stone-500">monthly gross income</span>
          <input data-testid="calc-income" type="number" min="0" value={income} onChange={(e) => setIncome(e.target.value)}
            placeholder="4,200" className="mt-1 w-full bg-stone-950 border border-stone-700 rounded-lg px-4 py-3 text-stone-100 font-mono focus:border-amber-500/60 outline-none" />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.25em] text-stone-500">children</span>
          <select data-testid="calc-children" value={children} onChange={(e) => setChildren(Number(e.target.value))}
            className="mt-1 w-full bg-stone-950 border border-stone-700 rounded-lg px-4 py-3 text-stone-100 font-mono focus:border-amber-500/60 outline-none">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        </label>
        <label className="flex items-end gap-2 pb-1">
          <input data-testid="calc-shared" type="checkbox" checked={shared} onChange={(e) => setShared(e.target.checked)} className="w-4 h-4 accent-amber-500" />
          <span className="text-[11px] text-stone-400">50/50 joint custody — the goal (NAC 425.115 cross-credit)</span>
        </label>
      </div>
      <p data-testid="calc-goal-note" className="text-[11px] text-emerald-400/90 border border-emerald-500/25 rounded-lg px-4 py-2.5 mb-6 leading-relaxed">
        Every model on this page treats equal custody as the goal. When both parents carry the children
        equally, the formula's own cross-credit leaves only the income gap in motion — at equal incomes,
        the transfer nets to zero and the household keeps its capital.
      </p>
      {shared && (
        <label className="block mb-6 max-w-xs">
          <span className="text-[10px] uppercase tracking-[0.25em] text-stone-500">other parent's monthly gross</span>
          <input data-testid="calc-other-income" type="number" min="0" value={otherIncome} onChange={(e) => setOtherIncome(e.target.value)}
            placeholder="2,800" className="mt-1 w-full bg-stone-950 border border-stone-700 rounded-lg px-4 py-3 text-stone-100 font-mono focus:border-amber-500/60 outline-none" />
        </label>
      )}
      <div className="space-y-1 font-mono text-sm">
        {rows.map((r, i) => (
          <div key={r.label} data-testid={`calc-bracket-${i}`} className="flex justify-between border-b border-stone-800/60 py-2">
            <span className="text-stone-400">{(r.rate * 100).toFixed(1)}% of {r.label} <span className="text-stone-600">({fmtUSD(r.base)})</span></span>
            <span className="text-amber-400">{fmtUSD(r.amount)}</span>
          </div>
        ))}
        <div data-testid="calc-total" className="flex justify-between pt-3 text-base">
          <span className="text-stone-200 font-semibold">guideline obligation</span>
          <span className="text-amber-300 font-bold">{fmtUSD(total)}</span>
        </div>
        {shared && (
          <div data-testid="calc-offset" className="flex justify-between pt-2 border-t border-amber-500/30 text-base">
            <span className="text-stone-200">50/50 offset — paid by {payer}</span>
            <span className="text-emerald-400 font-bold">{fmtUSD(offset)}</span>
          </div>
        )}
      </div>
      <p className="text-[10px] text-stone-500 mt-4 leading-relaxed">
        Straight NAC 425.140 bracket math. Courts may deviate on findings of fact (NAC 425.140(6)); a low-income
        schedule published annually by the Administrative Office of the Courts can apply instead (NAC 425.145);
        income may be imputed from earning capacity, not just paystubs (NAC 425.125). Educational arithmetic — not legal advice.
      </p>
    </div>
  );
};

/* ================= Discovery generator ================= */
const DEMANDS = [
  { id: "instrument", label: "Instrument of origination", cite: "NRS 104.3501",
    text: "Produce the original, unaltered instrument relied upon to originate the obligation ledger entry in this matter, and identify its maker, date of execution, and chain of custody." },
  { id: "holder", label: "Holder standing", cite: "NRS 104.3302",
    text: "Identify the present holder of any instrument said to be enforceable against the Respondent, and produce all records establishing holder-in-due-course status, including value given, good faith, and absence of notice of defense." },
  { id: "security", label: "Security agreement", cite: "NRS 104.9203",
    text: "Produce any security agreement bearing the Respondent's authenticated signature that purports to attach or perfect an interest in the Respondent's property, earnings, or accounts." },
  { id: "sdu", label: "Case-ledger accounting", cite: "42 U.S.C. § 654b",
    text: "Produce the complete State Disbursement Unit ledger attributable to this case: every receipt, hold, fee, and disbursement, with dates, amounts, and destination accounts." },
  { id: "ffp", label: "Federal draw attribution", cite: "42 U.S.C. § 655(a)",
    text: "Produce records identifying all federal financial participation drawn or claimed against administrative activity in this case, including the expenditure lines reported for federal matching." },
  { id: "incentive", label: "Incentive attribution", cite: "42 U.S.C. § 658a; 45 C.F.R. pt. 305",
    text: "Produce records showing this case's contribution to each performance measure reported for federal incentive payments: order establishment, current collections, arrearage collections, and cost-effectiveness." },
  { id: "registry", label: "Registry-fund handling", cite: "cf. N.C.G.S. § 7A-112 (comparative)",
    text: "If any funds in this matter are or were held in a court registry or clerk's custody, produce the investment records: instruments used, dates, yields, and the disposition of all earnings, together with the authority relied upon." },
  { id: "distribution", label: "Distribution breakdown", cite: "42 U.S.C. § 657",
    text: "Produce the distribution breakdown of all collections in this case between current support, arrearages, fees, and any amount retained as cost recovery, with the statutory authority for each retained amount." },
];

const DiscoveryGenerator = () => {
  const [fields, setFields] = useState({ obligor: "", counterparty: "", caseNo: "", deptNo: "" });
  const [selected, setSelected] = useState(() => new Set(DEMANDS.map((d) => d.id)));
  const [generated, setGenerated] = useState(false);
  const [sealing, setSealing] = useState(false);
  const [seal, setSeal] = useState("");
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const chosen = DEMANDS.filter((d) => selected.has(d.id));
  const downloadSealed = async () => {
    setSealing(true); setSeal("");
    try {
      const res = await fetch(`/api/tools/generate-ledger-demand`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          obligor_name: fields.obligor || "Respondent", counterparty: fields.counterparty || undefined,
          case_no: fields.caseNo || "UNFILED", dept_no: fields.deptNo,
          demand_ids: chosen.map((d) => d.id),
        }),
      });
      if (!res.ok) throw new Error("seal failed");
      setSeal(res.headers.get("X-Document-Seal") || "");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `BGNZN-DEMAND-${(res.headers.get("X-Document-Seal") || "sealed").slice(0, 8).toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* render path stays available */ } finally { setSealing(false); }
  };
  const input = "w-full bg-stone-950 border border-stone-700 rounded-lg px-4 py-2.5 text-stone-100 font-mono text-sm focus:border-amber-500/60 outline-none";
  return (
    <div data-testid="discovery-generator" className="rounded-2xl border border-violet-500/25 bg-stone-900/70 p-6 sm:p-8">
      <div className="grid sm:grid-cols-2 gap-4 mb-4">
        <input data-testid="dg-obligor" placeholder="Respondent name (obligor)" value={fields.obligor} onChange={(e) => setFields({ ...fields, obligor: e.target.value })} className={input} />
        <input data-testid="dg-counterparty" placeholder="Opposing party / agency" value={fields.counterparty} onChange={(e) => setFields({ ...fields, counterparty: e.target.value })} className={input} />
        <input data-testid="dg-case" placeholder="Case No." value={fields.caseNo} onChange={(e) => setFields({ ...fields, caseNo: e.target.value })} className={input} />
        <input data-testid="dg-dept" placeholder="Dept. No." value={fields.deptNo} onChange={(e) => setFields({ ...fields, deptNo: e.target.value })} className={input} />
      </div>
      <p className="text-[10px] uppercase tracking-[0.25em] text-stone-500 mb-2">demands to include</p>
      <div className="grid sm:grid-cols-2 gap-2 mb-6">
        {DEMANDS.map((d) => (
          <label key={d.id} data-testid={`dg-demand-${d.id}`} className="flex items-start gap-2 text-[12px] text-stone-300 cursor-pointer">
            <input type="checkbox" checked={selected.has(d.id)} onChange={() => toggle(d.id)} className="mt-0.5 w-4 h-4 accent-violet-500" />
            <span>{d.label} <span className="text-stone-500 font-mono text-[10px]">· {d.cite}</span></span>
          </label>
        ))}
      </div>
      <div className="flex gap-3 flex-wrap">
        <button data-testid="dg-generate" onClick={() => setGenerated(true)} disabled={!chosen.length}
          className="px-6 py-3 rounded-full font-semibold text-sm text-stone-950 transition-transform hover:scale-[1.03] active:scale-95"
          style={{ background: GOLD }}>
          GENERATE DOCUMENT
        </button>
        {generated && (
          <button data-testid="dg-print" onClick={() => window.print()}
            className="px-6 py-3 rounded-full font-semibold text-sm border border-stone-600 text-stone-200 hover:border-amber-500/60 transition-colors flex items-center gap-2">
            <Printer size={16} /> PRINT / SAVE PDF
          </button>
        )}
        {generated && (
          <button data-testid="dg-seal" onClick={downloadSealed} disabled={sealing}
            className="px-6 py-3 rounded-full font-semibold text-sm border border-violet-500/50 text-violet-200 hover:border-violet-400 transition-colors flex items-center gap-2">
            <FileSearch size={16} /> {sealing ? "SEALING…" : "SEALED PDF DOWNLOAD"}
          </button>
        )}
        {seal && (
          <span data-testid="dg-seal-hash" className="font-mono text-[10px] text-violet-300/80 self-center">
            integrity seal {seal.slice(0, 16)}… — sha256 of the rendered document
          </span>
        )}
      </div>
      {generated && (
        <div id="ivd-print-doc" data-testid="dg-document" className="mt-8 bg-white text-black rounded-lg p-8 sm:p-10 font-serif text-[13px] leading-relaxed">
          <div className="text-center font-bold">
            <p>EIGHTH JUDICIAL DISTRICT COURT</p>
            <p>FAMILY DIVISION</p>
            <p>CLARK COUNTY, NEVADA</p>
          </div>
          <div className="flex justify-between mt-8 mb-8">
            <div>
              <p className="uppercase">{fields.counterparty || "________________________"},</p>
              <p className="pl-8">Petitioner,</p>
              <p className="my-3">v.</p>
              <p className="uppercase">{fields.obligor || "________________________"},</p>
              <p className="pl-8">Respondent.</p>
            </div>
            <div className="text-right">
              <p>)</p><p>)</p><p>)&nbsp;&nbsp;&nbsp;Case No.: {fields.caseNo || "__________"}</p>
              <p>)</p><p>)&nbsp;&nbsp;&nbsp;Dept. No.: {fields.deptNo || "__________"}</p><p>)</p>
            </div>
          </div>
          <p className="text-center font-bold underline uppercase mb-6">
            Request for Production — Financial Origination, Ledger Accounting, and Registry Handling
          </p>
          <p className="mb-4">
            TO: {fields.counterparty || "the opposing party/agency"}, and its custodians of records. Respondent
            {" "}{(fields.obligor || "________________________")}, appearing in propria persona, requests production of the
            following records within the time allowed by rule. Each request is anchored to the authority noted.
          </p>
          <ol className="list-decimal pl-6 space-y-4">
            {chosen.map((d) => (
              <li key={d.id} data-testid={`dg-item-${d.id}`}>
                <span className="font-semibold">{d.label}</span> <span className="italic">({d.cite})</span> — {d.text}
              </li>
            ))}
          </ol>
          <div className="mt-10">
            <p>DATED this ____ day of ________________, 20____.</p>
            <p className="mt-6">Respectfully submitted,</p>
            <p className="mt-8">__________________________________</p>
            <p className="uppercase">{fields.obligor || "________________________"}, Respondent in propria persona</p>
          </div>
          <p className="mt-8 text-[10px] text-stone-500 border-t border-stone-300 pt-3">
            Formatting template generated for educational purposes by the BGNZN financial resource center. This is not
            legal advice and creates no attorney-client relationship. Have any filing reviewed by a licensed Nevada attorney.
          </p>
        </div>
      )}
    </div>
  );
};

/* ================= page ================= */
export default function IvDLedger() {
  const [openHop, setOpenHop] = useState("sdu");
  return (
    <div data-testid="ivd-ledger-page" className="relative min-h-screen bg-stone-950 text-stone-200 overflow-x-hidden">
      <style>{`@media print { body * { visibility: hidden; } #ivd-print-doc, #ivd-print-doc * { visibility: visible; } #ivd-print-doc { position: absolute; left: 0; top: 0; width: 100%; border-radius: 0; } }`}</style>
      <Particles />

      {/* hero */}
      <header className="relative max-w-5xl mx-auto px-6 pt-10 pb-16">
        <Link to="/" data-testid="ivd-back-link" className="inline-flex items-center gap-2 text-stone-500 hover:text-amber-400 text-xs uppercase tracking-[0.3em] transition-colors">
          <ArrowLeft size={14} /> the institute
        </Link>
        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-stone-100 mt-10"
          style={{ fontFamily: "Georgia, serif" }}
        >
          THE IV-D LEDGER
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 0.7 }}
          className="text-stone-400 max-w-2xl mt-6 leading-relaxed">
          Child support is a federal grant engine with a state formula bolted on. This page lays the money
          path open — every hop sourced to the statute that mandates it — then runs the Nevada math yourself,
          then prints the questions a litigant can lawfully ask. Beside it, the same functions built the
          private-contract way.
        </motion.p>
        <div className="mt-6 flex flex-wrap gap-2">
          <Cite>primary sources only</Cite>
          <Cite>educational — not legal advice</Cite>
          <Cite>trace it yourself</Cite>
        </div>
      </header>

      {/* BOOK ONE — the public engine */}
      <BookHead testid="book-one" kicker="BOOK ONE" title="Follow the Federal Dollar"
        sub="Six hops from a paycheck to the federal treasury. Tap any station to read the statute that built it." />
      <section className="relative max-w-3xl mx-auto px-6 pb-20">
        {HOPS.map((hop, i) => {
          const Icon = hop.icon;
          const open = openHop === hop.id;
          return (
            <motion.div key={hop.id} initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.5, delay: 0.05 }}>
              <button data-testid={`hop-${hop.id}`} onClick={() => setOpenHop(open ? null : hop.id)}
                className={`w-full text-left rounded-2xl border p-5 transition-colors ${open ? "border-amber-500/50 bg-stone-900/80" : "border-stone-800 bg-stone-900/40 hover:border-stone-700"}`}>
                <div className="flex items-center gap-4">
                  <span className="font-mono text-[10px] text-stone-500 w-6">{String(i + 1).padStart(2, "0")}</span>
                  <Icon size={18} className={open ? "text-amber-400" : "text-stone-500"} />
                  <div className="flex-1">
                    <p className={`font-mono text-sm tracking-widest ${open ? "text-amber-300" : "text-stone-200"}`}>{hop.label}</p>
                    <p className="text-[11px] text-stone-500">{hop.sub}</p>
                  </div>
                  <ChevronDown size={16} className={`text-stone-500 transition-transform ${open ? "rotate-180" : ""}`} />
                </div>
                {open && (
                  <div className="mt-4 pl-10 pr-2">
                    <p className="text-sm text-stone-300 leading-relaxed">{hop.body}</p>
                    <div className="mt-3">{hop.cites.map((c) => <Cite key={c}>{c}</Cite>)}</div>
                  </div>
                )}
              </button>
              {i < HOPS.length - 1 && (
                <div className="flex justify-start pl-12 py-1" aria-hidden="true">
                  <div className="w-px h-6 bg-gradient-to-b from-amber-500/50 to-transparent" />
                </div>
              )}
            </motion.div>
          );
        })}
      </section>

      {/* Nevada formula */}
      <BookHead testid="book-one-calc" kicker="BOOK ONE · TOOL" title="Run the Nevada Math"
        sub="NAC 425.140 is a bracket system, not a flat percentage. Three income brackets, rates set by child count. Run your own numbers — nothing leaves this page." />
      <section className="relative max-w-3xl mx-auto px-6 pb-20">
        <NevadaCalculator />
      </section>

      {/* BOOK TWO — the house parallel */}
      <BookHead testid="book-two" kicker="BOOK TWO" title="The Same Functions, Under Private Contract"
        sub="A member-owned resource center performs the same mechanical functions — intake, custody, registry, distribution of record — with no federal draw, no performance grant, and no public market. Side by side:" />
      <section className="relative max-w-5xl mx-auto px-6 pb-16">
        <div className="rounded-2xl border border-stone-800 bg-stone-900/40 overflow-hidden">
          <div className="grid grid-cols-2 font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500 border-b border-stone-800">
            <p className="px-5 py-3">the public engine</p>
            <p className="px-5 py-3 border-l border-stone-800 text-amber-500/80">the house ledger</p>
          </div>
          {[
            ["Centralized SDU funnel — one state intake", "Closed-loop settlement on chain 9940 — every transfer shows the member its own block number"],
            ["66¢ federal draw on every admin dollar (§ 655)", "No federal funds at all — the house runs on member capital and merchant clearing; there is no grant metric to feed"],
            ["Incentive pool scored on collection metrics (§ 658a)", "Backing registry — tangible assets absorbed with decommissioned cash, sealed on-chain, visible in the Ledger Room"],
            ["Clerk's discretion over resting registry funds (§ 7A-112)", "Vault custody bar by bar with attestation stamps (SKRs) — custody is recorded, never silently invested"],
            ["Administrative enforcement, contempt track (NRS 31A)", "Private-contract governance — high-value moves require 2-of-3 council signatures by text before any hardware signs"],
          ].map(([pub, house], i) => (
            <motion.div key={i} data-testid={`parallel-row-${i}`} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              transition={{ delay: i * 0.08 }} className="grid grid-cols-2 border-b border-stone-800/60 last:border-0">
              <p className="px-5 py-4 text-[13px] text-stone-400 leading-relaxed">{pub}</p>
              <p className="px-5 py-4 text-[13px] text-stone-200 leading-relaxed border-l border-stone-800/60">{house}</p>
            </motion.div>
          ))}
        </div>
        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          {[
            ["No public offering", "Membership is by private contract inside the business league. Nothing is offered to the public, so there is no public securities transaction to register."],
            ["Closed-loop stored value", "Units are spendable only at league registers and never trade on any market — a ledger of record between members, not an investment contract offered to investors."],
            ["No public customer funds", "No broker-dealer activity, no investment solicitation, no custody of the public's money — members see their own entries, and only their own."],
            ["Every movement leaves a witness", "Multi-sig council governance and an on-chain registry mean no silent discretion: what rests, what moves, and what yields is written down where the member can read it."],
          ].map(([t, b], i) => (
            <div key={i} data-testid={`perimeter-${i}`} className="rounded-xl border border-violet-500/20 bg-stone-900/50 p-5">
              <p className="font-mono text-xs tracking-widest text-violet-300 mb-2">{t}</p>
              <p className="text-[13px] text-stone-400 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
        <p className="text-[10px] text-stone-500 mt-4 leading-relaxed">
          Structural description of a private membership ledger, stated plainly for comparison. Regulatory
          perimeter turns on facts and counsel — this page is financial education, not a legal opinion.
        </p>
      </section>

      {/* registry discussion */}
      <section data-testid="registry-discussion" className="relative max-w-3xl mx-auto px-6 pb-20">
        <div className="rounded-2xl border border-amber-500/25 bg-stone-900/60 p-6 sm:p-8">
          <p className="font-mono text-xs tracking-widest text-amber-400 mb-3">WHERE THE COURT REGISTRY PLAYS ITS PART</p>
          <p className="text-sm text-stone-300 leading-relaxed">
            Support money does not always move straight through. Disputed amounts, undeliverable payments, and
            court-ordered deposits can rest in the clerk's registry — and resting funds are exactly what
            investment statutes govern. Under the comparative rule (N.C.G.S. § 7A-112), a single account over
            $10,000 expected to rest longer than six months <em>shall</em> be invested within 60 days, limited to
            U.S. obligations, state obligations, approved municipal paper, or insured deposits — with the State
            Auditor empowered to inspect and a misdemeanor attached to deviation. The statute commands the
            investment and names the instruments. It does not state on its face where the yield posts. That
            silence is the discovery question: whose money rested, in what instrument, at what yield, and who
            booked the earnings. In the house ledger the equivalent resting value — vaulted metal, registered
            assets — is recorded bar by bar and asset by asset, with the member able to read the entry that
            bears their name.
          </p>
          <div className="mt-3"><Cite>N.C.G.S. § 7A-112</Cite><Cite>42 U.S.C. § 654b</Cite></div>
        </div>
      </section>

      {/* statute vault — read the actual text */}
      <BookHead testid="statute-vault" kicker="THE STATUTE VAULT" title="Read the Text Yourself"
        sub="The primary sources behind every hop and every calculation — short verbatim passages, then what each one mechanically does." />
      <section className="relative max-w-5xl mx-auto px-6 pb-20 grid md:grid-cols-2 gap-4">
        {[
          ["42 U.S.C. § 654b — the funnel",
           "“The State agency shall establish and operate a unit (which shall be known as the 'State disbursement unit') for the collection and disbursement of payments under support orders.”",
           "One unit, every IV-D dollar through it — the chokepoint that makes case-level ledgers producible in discovery."],
          ["42 U.S.C. § 655(a) — the 66% match",
           "“The applicable percentage … is 66 percent for fiscal year 1990 and each fiscal year thereafter.”",
           "The federal treasury reimburses 66 cents of every legitimate administrative dollar the state spends on enforcement. The state carries 34."],
          ["42 U.S.C. § 658a — the incentive pool",
           "“There shall be paid to each State … an incentive payment” — scored on paternity establishment, order establishment, current collections (weighted 100%), arrearage collections and cost-effectiveness (capped at 75%).",
           "Performance pay: the state's score on collection metrics sizes its share of a federal pool. Data must survive audit, and payments must be reinvested in the program (§ 658a(f))."],
          ["42 U.S.C. § 657 — distribution",
           "Collected support is distributed by federal rule: current support to the family; in assistance cases, portions may be retained by the state as cost recovery.",
           "Who the collection actually pays is statute, not discretion — the split is producible line by line."],
          ["42 U.S.C. § 666(a)(1) — immediate withholding",
           "State plans must provide for immediate income withholding in IV-D cases — withholding begins without waiting for arrears.",
           "The dollar leaves the paycheck first; everything downstream is accounting."],
          ["NAC 425.140 — the Nevada brackets",
           "Monthly gross income in three brackets — first $6,000; $6,000–$10,000; over $10,000 — at 16/8/4% for one child, 22/11/6 for two, 26/13/6 for three, 28/14/7 for four, +2/+1/+0.5 per additional child.",
           "A bracket system, not a flat percentage — run it in the calculator above. Courts may deviate only on stated findings (425.140(6))."],
          ["NAC 425.115 — the 50/50 equilibrium",
           "In shared-custody cases each parent's guideline obligation is computed from their own income, and the difference is what moves.",
           "The cross-credit: at equal custody and equal incomes, the transfer nets to zero. This page's models treat that equilibrium as the goal."],
          ["NAC 425.125 / 425.145 — imputation & low income",
           "Income may be imputed from earning capacity, not only paystubs (425.125); a low-income schedule published annually by the Administrative Office of the Courts may apply instead (425.145).",
           "The two dials that move the number most after the bracket math itself."],
          ["N.C.G.S. § 7A-112 — registry investment (comparative)",
           "“When money in a single account in excess of ten thousand dollars ($10,000) is received by the clerk … and it can reasonably be expected that the money will remain on deposit … in excess of six months … the money exceeding ten thousand dollars ($10,000) shall be invested by the clerk within 60 days of receipt.”",
           "Resting registry money is investable by command of statute, in a fixed list of instruments, under State Auditor inspection — with a misdemeanor for deviation. Where the yield posts is the open question discovery exists to answer."],
        ].map(([title, quote, gloss], i) => (
          <motion.div key={i} data-testid={`vault-${i}`} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }} transition={{ duration: 0.45, delay: (i % 2) * 0.08 }}
            className="rounded-xl border border-stone-800 bg-stone-900/50 p-5 hover:border-amber-500/30 transition-colors">
            <p className="font-mono text-xs tracking-widest text-amber-400 mb-3">{title}</p>
            <p className="text-[13px] text-stone-300 leading-relaxed italic border-l-2 border-amber-500/30 pl-3 mb-3">{quote}</p>
            <p className="text-[12px] text-stone-500 leading-relaxed">{gloss}</p>
          </motion.div>
        ))}
      </section>

      {/* discovery generator */}
      <BookHead testid="book-three" kicker="BOOK THREE · TOOL" title="Ask the Ledger Questions"
        sub="Eight production demands, each anchored to the statute that makes the records exist. Fill the caption — Clark County, Eighth Judicial District, Family Division format — select the demands, print." />
      <section className="relative max-w-3xl mx-auto px-6 pb-24">
        <DiscoveryGenerator />
      </section>

      <footer className="relative border-t border-stone-800/60 py-10 text-center">
        <p className="text-[10px] uppercase tracking-[0.3em] text-stone-600">
          the iv-d ledger · a bgnzn financial resource center publication · educational use only
        </p>
      </footer>
    </div>
  );
}
